/**
 * @fileoverview Componente responsável por abrir as mensagens de um ticket em diálogo.
 */

import React, { useContext, useEffect, useRef, useState } from "react";

import api from "../../services/api";
import toastError from "../../errors/toastError";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  makeStyles,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { useHistory } from "react-router-dom";
import MessagesList from "../MessagesList";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import { SocketContext } from "../../context/Socket/SocketContext";

const drawerWidth = 320;
const DIALOG_WIDTH = 720;
const DIALOG_HEIGHT = 640;
const MIN_SKELETON_MS = 300;

const useStyles = makeStyles((theme) => ({
  paper: {
    width: DIALOG_WIDTH,
    height: DIALOG_HEIGHT,
    maxWidth: "92vw",
    maxHeight: "90vh",
    overflow: "hidden",
  },

  root: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    position: "relative",
    overflow: "hidden",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },

  skeletonWrap: {
    flex: 1,
    minHeight: 0,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    overflow: "hidden",
    background: theme.palette.type === "dark"
      ? "rgba(11, 13, 16, 0.6)"
      : theme.palette.background.default,
  },

  skeletonBubble: {
    borderRadius: 12,
    height: 44,
  },

  skeletonRowLeft: {
    display: "flex",
    justifyContent: "flex-start",
  },

  skeletonRowRight: {
    display: "flex",
    justifyContent: "flex-end",
  },
}));

const SKELETON_BUBBLES = [
  { side: "left", width: "60%" },
  { side: "right", width: "45%" },
  { side: "left", width: "40%" },
  { side: "right", width: "55%" },
  { side: "left", width: "70%" },
  { side: "right", width: "35%" },
];

const MessagesSkeleton = ({ classes }) => (
  <div className={classes.skeletonWrap}>
    {SKELETON_BUBBLES.map((b, i) => (
      <div
        key={i}
        className={b.side === "left" ? classes.skeletonRowLeft : classes.skeletonRowRight}
      >
        <Skeleton
          animation="wave"
          variant="rect"
          width={b.width}
          className={classes.skeletonBubble}
        />
      </div>
    ))}
  </div>
);

export default function TicketMessagesDialog({ open, handleClose, ticketId }) {
  const history = useHistory();
  const classes = useStyles();
  // Lido pelo carregamento sem entrar nas dependências dele (a função muda a cada desenho do pai).
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;

  const [, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    let minDelayTimer = null;
    const startedAt = Date.now();
    setLoading(true);

    const finishLoading = (apply) => {
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, MIN_SKELETON_MS - elapsed);
      minDelayTimer = setTimeout(() => {
        if (cancelled) return;
        apply();
        setLoading(false);
      }, remaining);
    };

    (async () => {
      try {
        // Quem vê a conversa é o servidor quem diz (nível da empresa): a que o
        // usuário não vê responde 403 e cai no aviso de erro abaixo.
        const { data } = await api.get("/tickets/" + ticketId);
        if (cancelled) return;

        finishLoading(() => {
          setContact(data.contact);
          setTicket(data);
        });
      } catch (err) {
        if (cancelled) return;
        finishLoading(() => {
          toastError(err);
          // Conversa que o usuário não vê: fecha a janela, com o aviso.
          if (err?.response?.status === 403 && handleCloseRef.current) handleCloseRef.current();
        });
      }
    })();

    return () => {
      cancelled = true;
      if (minDelayTimer) clearTimeout(minDelayTimer);
    };
  }, [ticketId, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);
    const currentTicketId = Number(ticketId);

    socket.on("ready", () => socket.emit("joinChatBox", `${currentTicketId}`));

    socket.on(`company-${companyId}-ticket`, (data) => {
      if (data.action === "update" && data.ticket.id === currentTicketId) {
        setTicket(data.ticket);
      }

      if (data.action === "delete" && data.ticketId === currentTicketId) {
        history.push("/tickets");
      }
    });

    socket.on(`company-${companyId}-contact`, (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId, history, open, socketManager]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const renderTicketInfo = () => {
    if (ticket.user !== undefined) {
      return (
        <TicketInfo
          contact={contact}
          ticket={ticket}
          onClick={handleDrawerOpen}
        />
      );
    }
  };

  const renderBody = () => {
    if (loading) return <MessagesSkeleton classes={classes} />;
    return (
      <Box className={classes.root}>
        <MessagesList
          ticket={ticket}
          ticketId={ticket.id}
          isGroup={ticket.isGroup}
          peek={true}
        />
      </Box>
    );
  };

  return (
    <Dialog
      maxWidth={false}
      onClose={handleClose}
      open={open}
      classes={{ paper: classes.paper }}
    >
      <TicketHeader loading={loading}>{renderTicketInfo()}</TicketHeader>
      <ReplyMessageProvider>{renderBody()}</ReplyMessageProvider>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
